import { Button, Form, Input, Radio, Space } from "antd";
import { useCallback, useEffect, useMemo, useRef } from "react";
import TemplateSelector from "./TemplateSelector";
import TextArea from "antd/es/input/TextArea";
import ContactSelector from "./ContactSelector";
import { colorsIcon, messageTypes } from "utils/static";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash-es";

const CreateChatForm = ({
  onSubmit,
  onCancel,
  state,
  contactOnly = false,
  inhouseChannelId,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const messageTextInputRef = useRef(null);

  const initialValues = useMemo(() => {
    return {
      receiver_type: "contact",
      message_type: "free_text",
      to_phone_number: "",
      contact_id: undefined,
      message_text: "",
      type_id: messageTypes.TEXT,
      attachment_key: "",
      attachment: [],
    };
  }, []);

  useEffect(() => {
    if (state?.id !== undefined) {
      form.setFieldsValue({
        contact_id: state.id,
      });
      setTimeout(() => {
        messageTextInputRef?.current.focus();
      }, 10);
    }
  }, [state, form, messageTextInputRef]);
  const selectedTemplateHandler = useCallback(
    (val, selected) => {
      if (!isEmpty(selected?.payload?.body)) {
        const templateBody = selected.payload.body.map((row, index) => ({
          value: row,
          id: index + 1,
          color: colorsIcon[index],
        }));
        form.setFieldValue("template_body", templateBody);
      }
      if (!isEmpty(selected?.payload?.header)) {
        const key = Object.keys(selected?.payload?.header)[0];
        form.setFieldsValue({ attachment_key: key, attachment: [] });
      }
    },
    [form]
  );

  // Channel inhouse tidak punya nomor WA; tujuan pesan memakai external_handle
  // kontak (mis. "kledo-user:234803") yang dikirim sebagai to_phone_number.
  // Form hanya menyimpan contact_id, jadi objek kontak terpilih disimpan di sini.
  const selectedContactRef = useRef(null);

  const contactChangeHandler = useCallback((_, option) => {
    selectedContactRef.current = option || null;
  }, []);

  const finishHandler = useCallback(
    (values) => {
      if (!contactOnly) {
        onSubmit(values);
        return;
      }
      onSubmit({
        ...values,
        to_phone_number:
          selectedContactRef.current?.external_handle ||
          state?.external_handle ||
          "",
      });
    },
    [contactOnly, onSubmit, state]
  );

  const numberValueFromEventHandler = useCallback((e) => {
    return e.target.value.replace(/\D/g, "");
  }, []);

  return (
    <Form
      layout="vertical"
      initialValues={initialValues}
      form={form}
      name="createChat"
      onFinish={finishHandler}
    >
      {/* contactOnly: pilihan tujuan disembunyikan (tetap "contact"), label
          "Tujuan" pindah ke pemilih kontak. */}
      <Form.Item name="receiver_type" label={t("to")} hidden={contactOnly}>
        <Radio.Group>
          <Radio.Button value="contact">{t("contact")}</Radio.Button>
          <Radio.Button value="number">{t("number")}</Radio.Button>
        </Radio.Group>
      </Form.Item>
      <Form.Item
        shouldUpdate={(prevValues, currentValues) =>
          prevValues.receiver_type !== currentValues.receiver_type
        }
        noStyle
      >
        {({ getFieldValue }) => {
          const receiverType = getFieldValue("receiver_type");
          if (receiverType === "number") {
            return (
              <Form.Item
                name="to_phone_number"
                rules={[
                  {
                    required: true,
                    message: t("please_input_the_number"),
                  },
                  {
                    max: 20,
                    message: t("max_characters", { value: 20 }),
                  },
                  {
                    min: 7,
                    message: t("min_characters", { value: 7 }),
                  },
                  // {
                  //   pattern: /^[\d]+$/,
                  //   message: t("only_number_allowed"),
                  // },
                ]}
                getValueFromEvent={numberValueFromEventHandler}
              >
                <Input autoFocus placeholder={`${t("number")} HP`} />
              </Form.Item>
            );
          }
          return (
            <ContactSelector
              label={contactOnly ? t("to") : undefined}
              defaultValue={state}
              inhouseChannelId={inhouseChannelId}
              autocompleteProps={{ onChange: contactChangeHandler }}
              rules={[
                {
                  required: true,
                  message: t("please_select_contact"),
                },
              ]}
            />
          );
        }}
      </Form.Item>
      <Form.Item name="message_type" label={t("message")}>
        <Radio.Group>
          <Radio.Button value="free_text">Free Text</Radio.Button>
          <Radio.Button value="template">Template</Radio.Button>
        </Radio.Group>
      </Form.Item>
      <Form.Item
        shouldUpdate={(prevValues, currentValues) =>
          prevValues.message_type !== currentValues.message_type
        }
        noStyle
      >
        {({ getFieldValue }) => {
          const messageType = getFieldValue("message_type");
          if (messageType === "free_text") {
            return (
              <Form.Item
                rules={[
                  {
                    required: true,
                    message: t("please_input_message"),
                  },
                ]}
                name="message_text"
              >
                <TextArea
                  ref={messageTextInputRef}
                  placeholder={t("message_content")}
                  rows={4}
                />
              </Form.Item>
            );
          }
          return (
            <TemplateSelector
              rules={[
                {
                  required: true,
                  message: t("please_select_template"),
                },
              ]}
              onSelect={selectedTemplateHandler}
            />
          );
        }}
      </Form.Item>
      <Form.Item className="mt-8">
        <Space>
          <Button htmlType="submit" type="primary">
            {t("button.send")}
          </Button>
          <Button onClick={onCancel}>{t("button.cancel")}</Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default CreateChatForm;
